import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/authContext";
import { RoleProvider } from "@/lib/roleContext";
import { FanProvider } from "@/lib/fanContext";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/layout/PublicLayout";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";
import FanLayout from "@/pages/fan/FanLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import FanPassportGate from "@/components/FanPassportGate";

import Discover from "@/pages/public/Discover";
import ArtistsDirectory from "@/pages/public/ArtistsDirectory";
import EventsDirectory from "@/pages/public/EventsDirectory";
import ArtistPage from "@/pages/public/ArtistPage";
import EventPage from "@/pages/public/EventPage";
import VenuesDirectory from "@/pages/public/VenuesDirectory";
import VenuePage from "@/pages/public/VenuePage";

import SignIn from "@/pages/auth/SignIn";
import SignUp from "@/pages/auth/SignUp";
import Forgot from "@/pages/auth/Forgot";
import Reset from "@/pages/auth/Reset";
import GoogleCallback from "@/pages/auth/GoogleCallback";
import AccountSettings from "@/pages/auth/AccountSettings";
import AcceptInvite from "@/pages/auth/AcceptInvite";

import ArtistSetup from "@/pages/onboarding/ArtistSetup";
import OrgSetup from "@/pages/onboarding/OrgSetup";
import OrgWorkspace from "@/pages/org/OrgWorkspace";
import OrgPublic from "@/pages/public/OrgPublic";

import AdminLayout from "@/components/layout/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminArtists from "@/pages/admin/AdminArtists";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminVenues from "@/pages/admin/AdminVenues";
import AdminInvitations from "@/pages/admin/AdminInvitations";
import AdminModeration from "@/pages/admin/AdminModeration";
import AdminDigests from "@/pages/admin/AdminDigests";
import AdminVerification from "@/pages/admin/AdminVerification";

import Overview from "@/pages/workspace/Overview";
import WorkspaceEvents from "@/pages/workspace/WorkspaceEvents";
import EventEditor from "@/pages/workspace/EventEditor";
import Audience from "@/pages/workspace/Audience";
import CheckIn from "@/pages/workspace/CheckIn";
import ArtistPageEditor from "@/pages/workspace/ArtistPageEditor";
import Business from "@/pages/workspace/Business";
import Team from "@/pages/workspace/Team";
import Settings from "@/pages/workspace/Settings";
import EventPerformance from "@/pages/workspace/EventPerformance";

import VenueLayout from "@/components/layout/VenueLayout";
import VenueOverview from "@/pages/venue/VenueOverview";
import VenueEvents from "@/pages/venue/VenueEvents";
import VenueEventEditor from "@/pages/venue/VenueEventEditor";
import VenueCheckIn from "@/pages/venue/VenueCheckIn";
import VenueAudience from "@/pages/venue/VenueAudience";
import VenueProfileEditor from "@/pages/venue/VenueProfileEditor";
import VenueTeam from "@/pages/venue/VenueTeam";
import VenueSettings from "@/pages/venue/VenueSettings";
import VenueTemplates from "@/pages/venue/VenueTemplates";
import VenueTemplateEditor from "@/pages/venue/VenueTemplateEditor";

import FanOverview from "@/pages/fan/FanOverview";
import FanFollowing from "@/pages/fan/FanFollowing";
import FanFollowingVenues from "@/pages/fan/FanFollowingVenues";
import FanEvents from "@/pages/fan/FanEvents";
import FanProfile from "@/pages/fan/FanProfile";

const WORKSPACE_ROLES = ["artist_owner", "artist_team", "event_staff", "admin"];

// Workspace access gate — allows either:
//   • an elevated global role (artist_owner / artist_team / event_staff / admin), OR
//   • any user whose account holds at least one artist_members row
//     (universal fan accounts can now also manage artist workspaces without
//      being globally re-tagged as artist_owner).
function WorkspaceGate({ children }) {
    const { user, artists } = useAuth();
    const location = useLocation();

    if (user === null) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center font-mono text-xs text-[#666]" data-testid="auth-checking">
                Checking session…
            </div>
        );
    }
    if (!user) {
        const next = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/signin?next=${next}`} replace />;
    }
    const roleAllowed = user.role && (WORKSPACE_ROLES.includes(user.role) || user.role === "admin");
    const hasMembership = Array.isArray(artists) && artists.length > 0;
    if (!roleAllowed && !hasMembership) {
        return (
            <div className="p-10 text-center" data-testid="workspace-empty-state">
                <div className="font-display font-black uppercase text-3xl text-[#ff4400]">No artist workspace yet</div>
                <div className="mt-2 text-sm text-[#a3a3a3]">Set up an Artist profile to open a workspace on this account.</div>
                <a href="/onboarding/artist" className="pm-btn pm-btn-primary mt-6 inline-flex" data-testid="workspace-goto-onboarding">
                    Start Artist setup
                </a>
            </div>
        );
    }
    return children;
}

function AppRouter() {
    return (
        <Routes>
            <Route element={<PublicLayout />}>
                <Route path="/" element={<Discover />} />
                <Route path="/artists" element={<ArtistsDirectory />} />
                <Route path="/artists/:id" element={<ArtistPage />} />
                <Route path="/events" element={<EventsDirectory />} />
                <Route path="/events/:id" element={<EventPage />} />
                <Route path="/venues" element={<VenuesDirectory />} />
                <Route path="/venues/:id" element={<VenuePage />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot" element={<Forgot />} />
                <Route path="/reset" element={<Reset />} />
                <Route path="/auth/google" element={<GoogleCallback />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route
                    path="/onboarding/artist"
                    element={
                        <ProtectedRoute>
                            <ArtistSetup />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/onboarding/:kind"
                    element={
                        <ProtectedRoute>
                            <OrgSetup />
                        </ProtectedRoute>
                    }
                />
                <Route path="/promoters/:orgId" element={<OrgPublic kind="promoter" />} />
                <Route path="/labels/:orgId" element={<OrgPublic kind="label" />} />
                <Route
                    path="/account"
                    element={
                        <ProtectedRoute>
                            <AccountSettings />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route
                path="/fan"
                element={
                    <ProtectedRoute>
                        <FanPassportGate>
                            <FanLayout />
                        </FanPassportGate>
                    </ProtectedRoute>
                }
            >
                <Route index element={<FanOverview />} />
                <Route path="following" element={<FanFollowing />} />
                <Route path="venues" element={<FanFollowingVenues />} />
                <Route path="events" element={<FanEvents />} />
                {/*
                  Legacy /fan/discover route: redirect to the main public Home/Discover so
                  passport users never dead-end. FanDiscover.jsx is preserved on disk for
                  reference but no longer routed — its backend recommendation endpoints
                  (fanDiscover / fanRecommendedVenues) are still consumed by FanOverview.
                */}
                <Route path="discover" element={<Navigate to="/" replace />} />
                <Route path="profile" element={<FanProfile />} />
            </Route>

            <Route
                path="/workspace"
                element={
                    <WorkspaceGate>
                        <WorkspaceLayout />
                    </WorkspaceGate>
                }
            >
                <Route index element={<Overview />} />
                <Route path="events" element={<WorkspaceEvents />} />
                <Route path="events/new" element={<EventEditor />} />
                <Route path="events/:id/edit" element={<EventEditor />} />
                <Route path="events/:id/performance" element={<EventPerformance />} />
                <Route path="audience" element={<Audience />} />
                <Route path="check-in" element={<CheckIn />} />
                <Route path="check-in/:eventId" element={<CheckIn />} />
                <Route path="artist-page" element={<ArtistPageEditor />} />
                <Route path="business" element={<Business />} />
                <Route path="team" element={<Team />} />
                <Route path="settings" element={<Settings />} />
            </Route>

            <Route
                path="/venue"
                element={
                    <ProtectedRoute>
                        <VenueLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<div />} />
                <Route path=":venueId" element={<VenueLayout />}>
                    <Route index element={<VenueOverview />} />
                    <Route path="overview" element={<VenueOverview />} />
                    <Route path="events" element={<VenueEvents />} />
                    <Route path="events/new" element={<VenueEventEditor />} />
                    <Route path="events/:eid/edit" element={<VenueEventEditor />} />
                    <Route path="templates" element={<VenueTemplates />} />
                    <Route path="templates/new" element={<VenueTemplateEditor />} />
                    <Route path="templates/:tid/edit" element={<VenueTemplateEditor />} />
                    <Route path="check-in" element={<VenueCheckIn />} />
                    <Route path="check-in/:eid" element={<VenueCheckIn />} />
                    <Route path="audience" element={<VenueAudience />} />
                    <Route path="profile" element={<VenueProfileEditor />} />
                    <Route path="team" element={<VenueTeam />} />
                    <Route path="settings" element={<VenueSettings />} />
                </Route>
            </Route>

            <Route
                path="/admin"
                element={
                    <ProtectedRoute roles={["admin"]}>
                        <AdminLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<AdminOverview />} />
                <Route path="artists" element={<AdminArtists />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="venues" element={<AdminVenues />} />
                <Route path="invitations" element={<AdminInvitations />} />
                <Route path="digests" element={<AdminDigests />} />
                <Route path="verification" element={<AdminVerification />} />
                <Route path="moderation" element={<AdminModeration />} />
            </Route>

            <Route
                path="/promoter/:orgId/*"
                element={
                    <ProtectedRoute>
                        <OrgWorkspace kind="promoter" />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/label/:orgId/*"
                element={
                    <ProtectedRoute>
                        <OrgWorkspace kind="label" />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function AppInner() {
    useAuth();
    return <AppRouter />;
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <RoleProvider>
                    <FanProvider>
                        <div className="grain min-h-screen bg-[#0a0a0a] text-[#f4f4f0]">
                            <AppInner />
                            <Toaster position="bottom-right" theme="dark" />
                        </div>
                    </FanProvider>
                </RoleProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
